import { ONBOARDING_STEPS, onboardingStepChange } from '@/model/onboarding/onboarding.model';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/useStore';
import { useTrans } from '@/shared/hooks/useTranslation';
import { isSourceConnected, useEtlSourcesQuery } from '@/viewmodel/features/etlSource';
import { toOnboardingSteps } from '@/viewmodel/features/onboarding/mappers/onboardingSteps';
import {
  advanceOnboardingStep,
  selectOnboardingStepIndex,
  setOnboardingStep,
} from '@/viewmodel/features/onboarding/state/onboardingProgressSlice';
import type { OnboardingStepView } from '@/viewmodel/features/onboarding/types';
import { selectCurrentProject } from '@/viewmodel/features/project';
import { EXPLORED_TABLES_EVENT, hasExploredTables } from '@/viewmodel/shared/onboarding';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export function useOnboardingSteps(): OnboardingStepView[] {
  const { t } = useTrans();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const stepIndex = useAppSelector(selectOnboardingStepIndex);
  const projectId = useAppSelector(selectCurrentProject)?.id ?? '';

  const { sources } = useEtlSourcesQuery(projectId);
  const hasConnectedSource = sources.some(isSourceConnected);

  const [hasExploredTable, setHasExploredTable] = useState(() => hasExploredTables(projectId));

  useEffect(() => {
    if (!projectId) return;
    const sync = () => setHasExploredTable(hasExploredTables(projectId));
    globalThis.addEventListener(EXPLORED_TABLES_EVENT, sync);
    globalThis.addEventListener('storage', sync);
    return () => {
      globalThis.removeEventListener(EXPLORED_TABLES_EVENT, sync);
      globalThis.removeEventListener('storage', sync);
    };
  }, [projectId]);

  useEffect(() => {
    const change = onboardingStepChange(pathname, { hasConnectedSource, hasExploredTable });
    if (change === null) return;
    dispatch(
      change.kind === 'set' ? setOnboardingStep(change.index) : advanceOnboardingStep(change.index),
    );
  }, [pathname, hasConnectedSource, hasExploredTable, dispatch]);

  return toOnboardingSteps(
    ONBOARDING_STEPS.map((key) => t(`onboarding.steps.${key}`)),
    stepIndex,
  );
}
